import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useUserProfile } from '@/hooks/useUserProfile'
import { usePermissions } from '@/hooks/usePermissions'
import { useTalentPoolAccess } from '@/hooks/useTalentPoolAccess'
import { useInboxAccess, useInboxCounts, useInboxLive } from '@/lib/inbox'
import { AnalyticsGlyph, AtsGlyph, DashboardGlyph, InboxGlyph, PeopleGlyph, ReferencesModuleGlyph, SignGlyph, type ModuleGlyph } from '@/components/icons/ModuleGlyphs'
import { SettingsGlyph } from '@/components/icons/SettingsGlyph'


export type AppSection = 'home' | 'ats' | 'crm' | 'inbox' | 'references' | 'analytics' | 'people' | 'sign' | 'settings' | 'my-profile' | null

const ATS_PREFIXES = ['/find', '/jobs', '/candidates', '/pipeline', '/calendar']
const CRM_PREFIXES = ['/crm']
const INBOX_PREFIXES = ['/inbox']
const REFERENCES_PREFIXES = ['/references']
const ANALYTICS_PREFIXES = ['/analytics', '/talent-intelligence', '/insights']
const PEOPLE_PREFIXES = ['/os']
const SIGN_PREFIXES = ['/sign']

export function getActiveSection(pathname: string, search = ''): AppSection {
  if (pathname === '/' || pathname === '/dashboard') return 'home'
  if (pathname === '/me' || pathname.startsWith('/me/')) return 'my-profile'
  if (ANALYTICS_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'))) return 'analytics'
  if (PEOPLE_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'))) return 'people'
  if (SIGN_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'))) return 'sign'
  if (REFERENCES_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'))) return 'references'
  if (INBOX_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'))) return 'inbox'
  if (CRM_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'))) return 'crm'
  if (ATS_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'))) return 'ats'
  if (pathname === '/settings') return 'settings'
  return null
}

type IconRenderer = ModuleGlyph
const ReferencesGlyph = ReferencesModuleGlyph

const allItems: Array<{ id: Exclude<AppSection, null | 'my-profile' | 'settings' | 'sign'>; label: string; Icon: IconRenderer; href: string; show: (p: ReturnType<typeof usePermissions>) => boolean }> = [
  { id: 'home', label: 'Dashboard', Icon: DashboardGlyph, href: '/dashboard', show: (p) => !p.isSalesUser },
  { id: 'ats', label: 'ATS', Icon: AtsGlyph, href: '/jobs', show: (p) => p.canViewJobs },
  // Inbox: admins and Recruiter/Sourcer seats on open jobs (inbox_access), checked below.
  { id: 'inbox', label: 'Inbox', Icon: InboxGlyph, href: '/inbox', show: () => false },
  { id: 'references', label: 'Reference checks', Icon: ReferencesGlyph, href: '/references', show: (p) => p.canViewReferences },
  { id: 'analytics', label: 'Analytics', Icon: AnalyticsGlyph, href: '/analytics', show: (p) => !p.isSalesUser && !p.isExternal },
]

// Tile classes — state-aware. The accent shape inside the glyph receives lilac
// fill only on hover/active; inactive keeps it at currentColor (signal OFF).
const tileBase =
  'flex h-11 w-11 items-center justify-center rounded-xl transition-colors duration-[var(--dur-hover)]'
const tileActive =
  'bg-[#fffcf9] text-[#0d0d09] [&_.accent]:fill-[#D7C5FB]'
const tileInactive =
  // gio-rail-tile: the signal turns lilac on hover (rule in index.css, mouse only).
  'gio-rail-tile text-[rgba(255,252,249,0.72)] hover:bg-white/[0.08] hover:text-[#fffcf9]'

export function AppSidebar() {
  const { pathname, search } = useLocation()
  const active = getActiveSection(pathname, search)
  const { profile } = useUserProfile()
  const permissions = usePermissions()
  const { data: canUseInbox = false } = useInboxAccess()
  const talentPool = useTalentPoolAccess() !== false
  const items = allItems
    .filter((item) => (item.id === 'inbox' ? canUseInbox : item.show(permissions)))
    // Job-based access: references and analytics work on the talent pool.
    .filter((item) => talentPool || (item.id !== 'references' && item.id !== 'analytics'))
  // The unread number on the Inbox glyph stays live on every page.
  useInboxLive(canUseInbox)
  const { data: inboxCounts } = useInboxCounts(canUseInbox)
  const inboxUnread = inboxCounts?.inbox_unread ?? 0

  const initials = (() => {
    const f = profile?.first_name?.trim()?.[0] ?? ''
    const l = profile?.last_name?.trim()?.[0] ?? ''
    const combined = `${f}${l}`.toUpperCase()
    if (combined) return combined
    return profile?.email?.[0]?.toUpperCase() ?? '?'
  })()

  return (
    <TooltipProvider delayDuration={150}>
      <aside
        className="gio-strip-follow hidden sm:flex fixed top-[calc(var(--gio-strip)+0.75rem)] left-3 bottom-3 z-[60] w-16 flex-col items-center py-3"
        style={{
          backgroundColor: '#0d0d09',
          borderRadius: 16,
          boxShadow:
            '0 8px 24px rgba(15,18,34,0.08), inset 0 0 0 1px rgba(255,252,249,0.06)',
          rowGap: 6,
        }}
        aria-label="Primary"
      >
        {items.map(item => {
          const { Icon } = item
          const isActive = active === item.id
          return (
            <div key={item.id} className="flex flex-col items-center" style={{ rowGap: 6 }}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={item.href}
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={item.id === 'inbox' && inboxUnread > 0 ? `${item.label}, ${inboxUnread} unread` : item.label}
                    className={cn(tileBase, isActive ? tileActive : tileInactive, 'relative')}
                  >
                    <Icon className="h-6 w-6" />
                    {item.id === 'inbox' && inboxUnread > 0 && (
                      <span
                        aria-hidden
                        className="absolute top-0.5 right-0.5 min-w-[17px] h-[17px] px-1 rounded-full bg-[#D7C5FB] text-[#0d0d09] font-poppins font-semibold text-[10px] leading-none tabular-nums flex items-center justify-center ring-2 ring-[#0d0d09]"
                      >
                        {inboxUnread > 99 ? '99+' : inboxUnread}
                      </span>
                    )}
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={12}>
                  {item.label}
                </TooltipContent>
              </Tooltip>
              {item.id === 'home' && (
                <div
                  aria-hidden
                  style={{
                    width: 28,
                    height: 1,
                    backgroundColor: 'rgba(255,252,249,0.14)',
                    marginTop: 2,
                    marginBottom: 2,
                  }}
                />
              )}
            </div>
          )
        })}

        {/* Workspace tools: People & Access sits apart from the hiring modules, after a divider. */}
        {(permissions.isPlatformAdmin || permissions.isAdmin) && (
          <>
            <div
              aria-hidden
              style={{ width: 28, height: 1, backgroundColor: 'rgba(255,252,249,0.14)', marginTop: 2, marginBottom: 2 }}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/os"
                  aria-current={active === 'people' ? 'page' : undefined}
                  aria-label="People & Access"
                  className={cn(tileBase, active === 'people' ? tileActive : tileInactive)}
                >
                  <PeopleGlyph className="h-6 w-6" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={12}>
                People &amp; Access
              </TooltipContent>
            </Tooltip>
            {/* Sign: envelopes and templates. Admins only, like People & Access. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/sign"
                  aria-current={active === 'sign' ? 'page' : undefined}
                  aria-label="Sign"
                  className={cn(tileBase, active === 'sign' ? tileActive : tileInactive)}
                >
                  <SignGlyph className="h-6 w-6" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={12}>
                Sign
              </TooltipContent>
            </Tooltip>
          </>
        )}

        {/* Bottom group: Settings + user avatar */}
        <div className="mt-auto flex flex-col items-center" style={{ rowGap: 6 }}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/settings"
                aria-current={active === 'settings' ? 'page' : undefined}
                aria-label="Settings"
                className={cn(tileBase, active === 'settings' ? tileActive : tileInactive, 'group')}
              >
                {active === 'settings' ? (
                  <SettingsGlyph size={20} color="#0d0d09" accent="#D7C5FB" />
                ) : (
                  <span className="relative h-5 w-5" aria-hidden>
                    <span className="absolute inset-0 transition-opacity group-hover:opacity-0">
                      <SettingsGlyph size={20} color="rgba(255,252,249,0.72)" accent="rgba(255,252,249,0.72)" />
                    </span>
                    <span className="absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100">
                      <SettingsGlyph size={20} color="#fffcf9" accent="#fffcf9" />
                    </span>
                  </span>
                )}
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={12}>
              Settings
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/me"
                aria-current={active === 'my-profile' ? 'page' : undefined}
                aria-label="My Profile"
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full transition-shadow duration-hover',
                  active === 'my-profile'
                    ? 'ring-2 ring-[#fffcf9] ring-offset-2 ring-offset-[#0d0d09]'
                    : 'hover:ring-2 hover:ring-white/30 hover:ring-offset-2 hover:ring-offset-[#0d0d09]'
                )}
              >
                <Avatar className="h-9 w-9">
                  {profile?.avatar_url && <AvatarImage src={profile.avatar_url} alt="" />}
                  <AvatarFallback className="bg-white/10 text-white text-xs font-medium">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={12}>
              My Profile
            </TooltipContent>
          </Tooltip>
        </div>
      </aside>
    </TooltipProvider>
  )
}
